"use client";

import { useAuth } from "@clerk/clerk-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getQuotaStatus,
  getSubscription,
  type QuotaStatus,
  type SubscriptionResponse,
} from "@/api/subscription.service";
import {
  createCheckoutSession,
  createPortalSession,
  STRIPE_PRICES,
  type PriceTier,
} from "@/api/stripe.service";

// Query keys
const SUBSCRIPTION_QUERY_KEY = ["subscription"];
const QUOTA_QUERY_KEY = ["quota"];

/**
 * Hook for fetching subscription details
 */
export function useSubscription() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<SubscriptionResponse>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: () => getSubscription(getToken),
    enabled: isSignedIn,
    staleTime: 1000 * 30, // 30 seconds — keeps data fresh after plan changes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for fetching quota status
 */
export function useQuotaStatus() {
  const { getToken, isSignedIn } = useAuth();

  return useQuery<QuotaStatus>({
    queryKey: QUOTA_QUERY_KEY,
    queryFn: () => getQuotaStatus(getToken),
    enabled: isSignedIn,
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for creating a checkout session and redirecting to Stripe
 */
export function useCheckout() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tier,
      priceId,
      successUrl,
      cancelUrl,
    }: {
      tier?: PriceTier;
      priceId?: string;
      successUrl?: string;
      cancelUrl?: string;
    }) => {
      const finalPriceId = priceId || (tier ? STRIPE_PRICES[tier] : undefined);
      if (!finalPriceId) {
        throw new Error("Either tier or priceId must be provided");
      }
      
      const checkoutUrl = await createCheckoutSession(finalPriceId, getToken, {
        successUrl,
        cancelUrl,
      });
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
      return checkoutUrl;
    },
    onSuccess: () => {
      // Invalidate subscription queries after checkout
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: QUOTA_QUERY_KEY });
    },
  });
}

/**
 * Hook for creating a portal session and redirecting to Stripe Customer Portal
 */
export function usePortal() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const portalUrl = await createPortalSession(getToken);
      
      // Redirect to Stripe Customer Portal
      window.location.href = portalUrl;
      return portalUrl;
    },
  });
}
